import type { AdminBooking, BookingPaymentMethod } from '@mds/shared/types/booking';

import Field, { inputCls, selectCls } from '@/components/ui/field';
import Select from '@mds/shared/ui/select';
import {
  DEPOSIT_PERCENTAGE,
  PAYMENT_METHOD_OPTIONS,
  collectableAmount,
  needsEligibility,
  type PaymentChoice,
  type PaymentDraft,
} from '@/domain/bookings';
import { formatCents } from '@/lib/money';
import type { FieldBinder } from '@/lib/validation';

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-base text-ink-muted">{label}</span>
      <span className="text-base font-semibold tabular-nums">{children}</span>
    </div>
  );
}

/**
 * The payment half of every money-taking form: what the booking owes, and the
 * three things the administrator gets to decide about settling it.
 *
 * The amount is never one of them. A first payment is the fixed 50% deposit or
 * the whole total, and after a deposit the balance goes in full — so the figure
 * is read off the booking the server holds rather than typed against it.
 */
export default function PaymentFields({
  booking,
  draft,
  onChange,
  field,
}: {
  booking: AdminBooking;
  draft: PaymentDraft;
  onChange: (next: PaymentDraft) => void;
  /** The page's validation, checked against `paymentSchema(booking)`. */
  field: FieldBinder;
}) {
  const { payment } = booking;
  const firstPayment = payment.paidAmountMinor === 0;
  const taking = collectableAmount(booking, draft.choice);

  const verify = needsEligibility(booking);

  return (
    <div className="flex flex-col gap-4">
      {verify && (
        <div className="flex flex-col gap-3 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3">
          <span className="text-base font-semibold text-warn-text">
            Student package: check eligibility first
          </span>
          {booking.price.package?.eligibility.instructions && (
            <span className="text-base text-warn-text">
              {booking.price.package.eligibility.instructions}
            </span>
          )}
          <Field
            label="What you checked"
            hint="Recorded on the booking with your name; no document is uploaded."
            required
            {...field('evidenceNote')}
          >
            <input
              className={inputCls}
              value={draft.evidenceNote}
              maxLength={500}
              placeholder="Current student card checked; expires June 2027."
              onChange={(e) => onChange({ ...draft, evidenceNote: e.target.value })}
            />
          </Field>
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-xl border border-hairline bg-raised px-4 py-3">
        <Line label="Booking total">{formatCents(payment.totalAmountMinor)}</Line>
        <Line label={`${DEPOSIT_PERCENTAGE}% deposit`}>
          {formatCents(payment.requiredDepositAmountMinor)}
        </Line>
        <Line label="Paid so far">{formatCents(payment.paidAmountMinor)}</Line>
        <Line label="Balance">
          <span className={payment.balanceAmountMinor > 0 ? 'text-warn-text' : 'text-ink-muted'}>
            {formatCents(payment.balanceAmountMinor)}
          </span>
        </Line>
      </div>

      {firstPayment ? (
        <Field label="Taking" required {...field('choice')}>
          <Select
            variant="field"
            className={selectCls}
            value={draft.choice}
            onChange={(choice) => onChange({ ...draft, choice: choice as PaymentChoice })}
            options={[
              { value: 'full', label: `Full amount: ${formatCents(payment.totalAmountMinor)}` },
              {
                value: 'deposit',
                label: `${DEPOSIT_PERCENTAGE}% deposit: ${formatCents(
                  payment.requiredDepositAmountMinor,
                )}`,
              },
            ]}
            aria-label="Taking"
          />
        </Field>
      ) : (
        <p className="m-0 text-base text-ink-secondary">
          The deposit has been taken, so the remaining {formatCents(taking)} is collected in full.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Method" required {...field('method')}>
          <Select
            variant="field"
            className={selectCls}
            value={draft.method}
            onChange={(method) =>
              onChange({
                ...draft,
                method: method as BookingPaymentMethod,
                // The API rejects a typed method on anything but `other`.
                methodDetail: method === 'other' ? draft.methodDetail : '',
              })
            }
            options={PAYMENT_METHOD_OPTIONS}
            aria-label="Method"
          />
        </Field>
        {draft.method === 'other' && (
          <Field
            label="What was it"
            hint="Cheque, bank transfer, and so on."
            required
            {...field('methodDetail')}
          >
            <input
              className={inputCls}
              value={draft.methodDetail}
              maxLength={80}
              placeholder="Office cheque"
              onChange={(e) => onChange({ ...draft, methodDetail: e.target.value })}
            />
          </Field>
        )}
      </div>

      <Field
        label="Note"
        hint="Optional. Kept on the payment record, which cannot be edited."
        {...field('note')}
      >
        <input
          className={inputCls}
          value={draft.note}
          maxLength={500}
          placeholder="Deposit received at the front desk"
          onChange={(e) => onChange({ ...draft, note: e.target.value })}
        />
      </Field>

      <div className="flex items-center justify-between rounded-xl border border-hairline bg-raised px-4 py-3">
        <span className="text-base text-ink-muted">Recording now</span>
        <span className="text-xl font-semibold tabular-nums">{formatCents(taking)}</span>
      </div>
    </div>
  );
}
